import { create } from "zustand";
import { hc } from "@/frontend/lib/hc";

const TIMEZONE_COUNTRY_MAP: Record<string, string> = {
  "Asia/Kolkata": "in", "Asia/Calcutta": "in", "Asia/Mumbai": "in",
  "America/New_York": "us", "America/Chicago": "us", "America/Denver": "us",
  "America/Los_Angeles": "us", "America/Anchorage": "us", "Pacific/Honolulu": "us",
  "Europe/London": "gb", "Europe/Berlin": "de", "Europe/Paris": "fr",
  "Europe/Madrid": "es", "Europe/Rome": "it", "Europe/Amsterdam": "nl",
  "Europe/Moscow": "ru", "Asia/Tokyo": "jp", "Asia/Shanghai": "cn",
  "Asia/Hong_Kong": "hk", "Asia/Singapore": "sg", "Asia/Dubai": "ae",
  "Asia/Karachi": "pk", "Asia/Dhaka": "bd", "Asia/Jakarta": "id",
  "Asia/Bangkok": "th", "Asia/Seoul": "kr", "Asia/Manila": "ph",
  "Australia/Sydney": "au", "Australia/Melbourne": "au", "Pacific/Auckland": "nz",
  "America/Toronto": "ca", "America/Vancouver": "ca", "America/Sao_Paulo": "br",
  "America/Mexico_City": "mx", "Africa/Lagos": "ng", "Africa/Johannesburg": "za",
  "Africa/Cairo": "eg", "Asia/Riyadh": "sa", "Asia/Kuala_Lumpur": "my",
  "Asia/Ho_Chi_Minh": "vn", "Asia/Colombo": "lk", "Asia/Kathmandu": "np",
};

export function getCountryFromTimezone(): string {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return TIMEZONE_COUNTRY_MAP[tz] || "in";
  } catch {
    return "in";
  }
}

interface InfraStatus {
  exists: boolean;
  id?: number;
  status: string | null;
  requiresReprovision?: boolean;
  whatsappConnected: boolean;
  whatsappPhone: string | null;
  whatsappStatus: string | null;
  sidecarEndpoint: string | null;
  region: string | null;
  createdAt: string | null;
  updatedAt: string | null;
}

interface ProvisionError {
  message: string;
  code: string | null;
  requiredCredits?: number;
  availableCredits?: number;
  monthlyCredits?: number;
}

interface InfraState {
  infraStatus: InfraStatus | null;
  loading: boolean;
  provisioning: boolean;
  provisionError: ProvisionError | null;
  tearingDown: boolean;
  qrDataUrl: string | null;
  qrLoading: boolean;
  qrError: string | null;
  qrScanned: boolean;
  pairCode: string | null;
  pairLoading: boolean;
  pairError: string | null;

  disconnecting: boolean;

  fetchStatus: (orgId: string) => Promise<void>;
  provision: (orgId: string, country?: string) => Promise<boolean>;
  teardown: (orgId: string) => Promise<boolean>;
  disconnectWhatsApp: (orgId: string) => Promise<boolean>;
  /** showLoading marks a fetch the person asked for; background refreshes stay quiet. */
  fetchQR: (orgId: string, options?: { showLoading?: boolean }) => Promise<QrFetchResult>;
  /**
   * Show WhatsApp's current QR and fetch the next one as soon as it expires.
   * Returns a function that stops this refresh loop only.
   */
  startQrRefresh: (orgId: string) => () => void;
  /** Fetch a new QR now, e.g. from a "Refresh QR" button. */
  refreshQrNow: (orgId: string) => void;
  requestPairCode: (orgId: string, phone: string) => Promise<boolean>;
  reset: () => void;
}

interface QrFetchResult {
  qr: string | null;
  expiresInMs: number | null;
}

// WhatsApp only accepts the latest linking QR, so the next one is fetched right
// after the bridge says the current one expires.
const QR_EXPIRY_GRACE_MS = 500;
// The bridge has not seen WhatsApp's replacement yet; ask again shortly.
const QR_NOT_ROTATED_RETRY_MS = 2_000;
const QR_ERROR_RETRY_MS = 3_000;
// Bridges without expiry information: WhatsApp replaces the QR every 20s.
const QR_UNKNOWN_EXPIRY_REFRESH_MS = 15_000;

export function getNextQrFetchDelay(result: QrFetchResult): number {
  if (!result.qr) return QR_ERROR_RETRY_MS;
  if (result.expiresInMs === null) return QR_UNKNOWN_EXPIRY_REFRESH_MS;
  if (result.expiresInMs === 0) return QR_NOT_ROTATED_RETRY_MS;
  return result.expiresInMs + QR_EXPIRY_GRACE_MS;
}

interface QrRefreshLoop {
  refreshNow: () => void;
  stop: () => void;
}

const activeQrRefreshLoops = new Set<QrRefreshLoop>();

async function readInfraError(
  resp: Response
): Promise<ProvisionError> {
  const error = (await resp.json().catch(() => ({}))) as {
    error?: string;
    code?: string;
    requiredCredits?: number;
    availableCredits?: number;
    monthlyCredits?: number;
  };

  return {
    message: error.error || "Failed to start deployment",
    code: error.code || (resp.status === 403 ? "ORG_MEMBERSHIP_REQUIRED" : null),
    requiredCredits: error.requiredCredits,
    availableCredits: error.availableCredits,
    monthlyCredits: error.monthlyCredits,
  };
}

export const useInfraStore = create<InfraState>((set, get) => ({
  infraStatus: null,
  loading: false,
  provisioning: false,
  provisionError: null,
  tearingDown: false,
  disconnecting: false,
  qrDataUrl: null,
  qrLoading: false,
  qrError: null,
  qrScanned: false,
  pairCode: null,
  pairLoading: false,
  pairError: null,

  fetchStatus: async (orgId: string) => {
    set({ loading: true });
    try {
      const resp = await hc.api.infra.status.$get({
        query: { organizationId: orgId },
      });
      if (resp.ok) {
        const data = await resp.json();
        set((state) => ({
          infraStatus: data as InfraStatus,
          provisionError:
            state.provisionError?.code === "ORG_MEMBERSHIP_REQUIRED"
              ? null
              : state.provisionError,
        }));
      } else {
        set({ provisionError: await readInfraError(resp as Response) });
      }
    } catch (err) {
      console.error("[INFRA] Failed to fetch status:", err);
    } finally {
      set({ loading: false });
    }
  },

  provision: async (orgId: string, country?: string) => {
    set({ provisioning: true, provisionError: null });
    try {
      const proxyCountry = country || getCountryFromTimezone();

      const resp = await hc.api.infra.provision.$post({
        query: { organizationId: orgId, country: proxyCountry.toLowerCase() },
      });
      if (resp.ok) {
        await get().fetchStatus(orgId);
        set({ provisionError: null });
        return true;
      }
      set({ provisionError: await readInfraError(resp as Response) });
      return false;
    } catch (err) {
      console.error("[INFRA] Failed to provision:", err);
      set({
        provisionError: {
          message: "Failed to start deployment",
          code: null,
        },
      });
      return false;
    } finally {
      set({ provisioning: false });
    }
  },

  teardown: async (orgId: string) => {
    set({ tearingDown: true });
    try {
      const resp = await hc.api.infra.teardown.$post({
        query: { organizationId: orgId },
      });
      if (resp.ok) {
        await get().fetchStatus(orgId);
        set({ provisionError: null });
        return true;
      }
      return false;
    } catch (err) {
      console.error("[INFRA] Failed to teardown:", err);
      return false;
    } finally {
      set({ tearingDown: false });
    }
  },

  disconnectWhatsApp: async (orgId: string) => {
    set({ disconnecting: true });
    try {
      const resp = await hc.api.infra.disconnect.$post({
        query: { organizationId: orgId },
      });
      if (resp.ok) {
        await get().fetchStatus(orgId);
        return true;
      }
      return false;
    } catch (err) {
      console.error("[INFRA] Failed to disconnect WhatsApp:", err);
      return false;
    } finally {
      set({ disconnecting: false });
    }
  },

  fetchQR: async (orgId: string, options?: { showLoading?: boolean }) => {
    // Only the first load and requested refreshes show a spinner; the ones
    // that follow WhatsApp's 20s rotation would otherwise flicker the button.
    const showLoading = Boolean(options?.showLoading || !get().qrDataUrl);
    set({ ...(showLoading ? { qrLoading: true } : {}), qrError: null });
    try {
      const resp = await hc.api.infra.qr.$get({
        query: { organizationId: orgId },
      });
      if (resp.ok) {
        const data = (await resp.json()) as { qr?: string; expiresInMs?: number | null };
        const qr = data.qr || null;
        set({ qrDataUrl: qr, qrError: qr ? null : "QR code unavailable" });
        return { qr, expiresInMs: data.expiresInMs ?? null };
      }
      // The server shut the bridge down; reload status so the page offers Reconnect
      if (resp.status === 503) void get().fetchStatus(orgId);
      // A failed refresh is not evidence that the person scanned the code.
      // Keep the last QR visible and let connection status confirm linking.
      const err = (await resp.json()) as { error?: string };
      set({ qrError: err.error || "Failed to fetch QR" });
    } catch {
      set({ qrError: "Failed to connect to sidecar" });
    } finally {
      if (showLoading) set({ qrLoading: false });
    }
    return { qr: null, expiresInMs: null };
  },

  startQrRefresh: (orgId: string) => {
    let stopped = false;
    let inFlight = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const tick = async (showLoading = false) => {
      // A fresh QR is already on its way; a second request adds nothing.
      if (inFlight) return;
      inFlight = true;
      clearTimeout(timer);
      const result = await get().fetchQR(orgId, { showLoading });
      inFlight = false;
      if (stopped) return;
      timer = setTimeout(() => void tick(), getNextQrFetchDelay(result));
    };
    // Background tabs throttle timers, so the QR may be stale on return.
    const onVisible = () => {
      if (document.visibilityState === "visible") void tick();
    };

    const loop: QrRefreshLoop = {
      refreshNow: () => void tick(true),
      stop: () => {
        stopped = true;
        clearTimeout(timer);
        document.removeEventListener("visibilitychange", onVisible);
        activeQrRefreshLoops.delete(loop);
      },
    };
    activeQrRefreshLoops.add(loop);
    document.addEventListener("visibilitychange", onVisible);
    void tick();
    return loop.stop;
  },

  refreshQrNow: (orgId: string) => {
    if (activeQrRefreshLoops.size === 0) {
      void get().fetchQR(orgId, { showLoading: true });
      return;
    }
    activeQrRefreshLoops.forEach((loop) => loop.refreshNow());
  },

  requestPairCode: async (orgId: string, phone: string) => {
    set({ pairLoading: true, pairError: null, pairCode: null });
    try {
      const resp = await fetch(
        `/api/infra/pair?organizationId=${encodeURIComponent(orgId)}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ phone }),
        }
      );
      if (resp.ok) {
        const data = (await resp.json()) as { code?: string };
        set({ pairCode: data.code || null });
        return true;
      }
      if (resp.status === 503) void get().fetchStatus(orgId);
      const err = (await resp.json()) as { error?: string };
      set({ pairError: err.error || "Failed to get pairing code" });
      return false;
    } catch (err) {
      set({ pairError: "Failed to connect to sidecar" });
      return false;
    } finally {
      set({ pairLoading: false });
    }
  },

  reset: () => {
    activeQrRefreshLoops.forEach((loop) => loop.stop());
    set({
      infraStatus: null,
      loading: false,
      provisioning: false,
      provisionError: null,
      tearingDown: false,
      disconnecting: false,
      qrDataUrl: null,
      qrLoading: false,
      qrError: null,
      qrScanned: false,
      pairCode: null,
      pairLoading: false,
      pairError: null,
    });
  },
}));
